import React, { useState } from 'react';
import { Heart, Plus, Eye, Trash2, ShoppingCart, List, Package, CheckCircle } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import { Button } from '../ui';
import ViewLayout from '../ui/ViewLayout';

const ListsView = ({ currentUser, props, addToCart, userLists, setUserLists, onCreateList, onRemoveFromList, onDeleteList, refreshLists, cart, setCart, setProps }) => {
  const { toast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [selectedList, setSelectedList] = useState(null);

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.warning('Please enter a list name');
      return;
    }

    try {
      await onCreateList({
        name: newListName,
        description: newListDescription
      });
      setNewListName('');
      setNewListDescription('');
      setShowCreateModal(false);
      // Refresh lists to get the new one from database
      if (refreshLists) {
        const updatedLists = await refreshLists();
        if (updatedLists) {
          setUserLists(updatedLists);
        }
      }
    } catch (err) {
      toast.error('Failed to create list', { title: 'List Error' });
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await onDeleteList(listId);
      setUserLists(userLists.filter(list => list.id !== listId));
      if (selectedList?.id === listId) {
        setSelectedList(null);
      }
      toast.success('List deleted successfully!');
    } catch (err) {
      toast.error('Failed to delete list', { title: 'Delete Error' });
    }
  };

  const handleRemoveFromList = async (listId, propId) => {
    try {
      await onRemoveFromList(propId, listId);
      
      const updatedLists = userLists.map(list => {
        if (list.id === listId) {
          return {
            ...list,
            propIds: list.propIds.filter(id => id !== propId),
            updatedAt: new Date()
          };
        }
        return list;
      });
      
      setUserLists(updatedLists);
      
      // Update selectedList if it's the one being modified
      if (selectedList?.id === listId) {
        const updatedSelectedList = updatedLists.find(list => list.id === listId);
        setSelectedList(updatedSelectedList);
      }
    } catch (err) {
      toast.error('Failed to remove prop from list', { title: 'List Error' });
    }
  };

  const handleAddAllToCart = (list) => {
    const availableProps = list.propIds.filter(propId => {
      const prop = props.find(p => p.id === propId);
      return prop && prop.status === 'available';
    });
    
    // Filter out props that are already in cart
    const newPropsToAdd = availableProps.filter(propId => !cart.includes(propId));
    
    console.log('📦 Available props to add:', availableProps);
    console.log('📦 New props (not in cart):', newPropsToAdd);
    console.log('📦 Current cart:', cart);
    
    if (newPropsToAdd.length === 0) {
      toast.info('All available props from this list are already in your cart');
      return;
    }
    
    // Add all props to cart in a single state update
    setCart(prevCart => {
      const updatedCart = [...prevCart, ...newPropsToAdd];
      console.log('📦 Updated cart after batch add:', updatedCart);
      return updatedCart;
    });
    
    // Update props inCart status
    setProps(prevProps => 
      prevProps.map(p => 
        newPropsToAdd.includes(p.id) ? {...p, inCart: true} : p
      )
    );
    
    toast.success(`Added ${newPropsToAdd.length} available props to cart`);
  };

  const getListProps = (list) => {
    return list.propIds.map(propId => props.find(p => p.id === propId)).filter(Boolean);
  };

  if (selectedList) {
    const listProps = getListProps(selectedList);
    
    const individualListActions = [
      <Button
        key="add-all"
        onClick={() => handleAddAllToCart(selectedList)}
        variant="primary"
        size="compact"
        icon={ShoppingCart}
      >
        Add All to Cart
      </Button>,
      <Button
        key="delete"
        onClick={() => {
          setListToDelete(selectedList);
          setShowDeleteModal(true);
        }}
        variant="danger"
        size="compact"
        icon={Trash2}
      >
        Delete List
      </Button>
    ];
    
    const individualListStats = [
      <ViewLayout.Stat 
        title="Total Props" 
        value={listProps.length} 
        icon={Package} 
        color="blue" 
      />,
      <ViewLayout.Stat 
        title="Available" 
        value={listProps.filter(p => p.status === 'available').length} 
        icon={CheckCircle} 
        color="green" 
      />
    ];
    
    return (
      <>
        <ViewLayout
          title={selectedList.name}
          subtitle={selectedList.description}
          breadcrumbs={[
            { label: 'My Lists', onClick: () => setSelectedList(null) }
          ]}
          stats={individualListStats}
          actions={individualListActions}
        >
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Props in this List ({listProps.length})</h3>
            </div>
            <div className="p-4">
            {listProps.length > 0 ? (
              <div className="space-y-4">
                {listProps.map(prop => (
                  <div key={prop.id} className="flex gap-4 p-3 border rounded-lg">
                    <img
                      src={prop.images?.[0] || '/api/placeholder/300/200'}
                      alt={prop.name}
                      className="w-16 h-12 object-cover rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{prop.name}</h4>
                          <p className="text-sm text-gray-600">{prop.category}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            prop.status === 'available' ? 'bg-green-100 text-green-800' :
                            prop.status === 'checked_out' ? 'bg-orange-100 text-orange-800' :
                            prop.status === 'reserved' ? 'bg-purple-100 text-purple-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {prop.status}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {prop.status === 'available' && (
                            <Button
                              onClick={() => addToCart(prop.id)}
                              variant="primary"
                              size="compact"
                            >
                              Add to Cart
                            </Button>
                          )}
                          <Button
                            onClick={() => handleRemoveFromList(selectedList.id, prop.id)}
                            variant="secondary"
                            size="compact"
                          >
                            Remove from List
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Heart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No props in this list yet</p>
                <p className="text-sm mt-2">Add props to this list while browsing</p>
              </div>
            )}
          </div>
        </div>
      </ViewLayout>
      
      {/* Modals for individual list view */}
      {showDeleteModal && listToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-semibold text-lg mb-4 text-red-800">Delete List</h3>
            
            <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
              <div className="font-medium text-sm mb-2 text-red-900">🗑️ You are about to delete:</div>
              <div className="text-sm text-red-800 space-y-1">
                <div><span className="font-medium">List name:</span> {listToDelete.name}</div>
                {listToDelete.description && (
                  <div><span className="font-medium">Description:</span> {listToDelete.description}</div>
                )}
                <div><span className="font-medium">Props in list:</span> {listToDelete.propIds?.length || 0} items</div>
                <div><span className="font-medium">Last updated:</span> {listToDelete.updatedAt?.toLocaleDateString()}</div>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
              <div className="text-sm text-yellow-800">
                <div className="font-medium mb-1">⚠️ This action will:</div>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Permanently delete this list</li>
                  <li>Remove all props from this list</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  handleDeleteList(listToDelete.id);
                  setShowDeleteModal(false);
                  setListToDelete(null);
                }}
                variant="danger"
                size="compact"
                className="flex-1"
              >
                Delete List
              </Button>
              <Button
                onClick={() => {
                  setShowDeleteModal(false);
                  setListToDelete(null);
                }}
                variant="secondary"
                size="compact"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // Main lists view stats and actions
  const totalProps = userLists.reduce((sum, list) => sum + (list.propIds?.length || 0), 0);
  const availableProps = userLists.reduce((sum, list) => {
    const listProps = getListProps(list);
    return sum + listProps.filter(p => p.status === 'available').length;
  }, 0);

  const mainListsStats = [
    <ViewLayout.Stat 
      title="Total Lists" 
      value={userLists.length} 
      icon={List} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Total Props" 
      value={totalProps} 
      icon={Package} 
      color="purple" 
    />,
    <ViewLayout.Stat 
      title="Available Props" 
      value={availableProps} 
      icon={CheckCircle} 
      color="green" 
    />
  ];

  const mainListsActions = [
    <Button
      key="create"
      onClick={() => setShowCreateModal(true)}
      variant="primary"
      size="compact"
      icon={Plus}
    >
      Create New List
    </Button>
  ];

  return (
    <>
      <ViewLayout
        title="My Lists"
        subtitle="Organize props for different productions"
        stats={mainListsStats}
        actions={mainListsActions}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {userLists.map(list => {
          const listProps = getListProps(list);
          const availableCount = listProps.filter(p => p.status === 'available').length;
          
          return (
            <div key={list.id} className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-lg">{list.name}</h3>
                <button
                  onClick={() => {
                    setListToDelete(list);
                    setShowDeleteModal(true);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <p className="text-gray-600 text-sm mb-3">{list.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span>Total Props:</span>
                  <span className="font-medium">{listProps.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Available:</span>
                  <span className="font-medium text-green-600">{availableCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Last Updated:</span>
                  <span className="text-gray-500">{list.updatedAt.toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => setSelectedList(list)}
                  variant="primary"
                  size="compact"
                  icon={Eye}
                  className="flex-1"
                >
                  View
                </Button>
                <Button
                  onClick={() => handleAddAllToCart(list)}
                  disabled={availableCount === 0}
                  variant="success"
                  size="compact"
                  icon={ShoppingCart}
                  className="flex-1"
                >
                  Add All
                </Button>
              </div>
            </div>
          );
        })}
      </div>

        {userLists.length === 0 && (
        <div className="text-center py-12 text-gray-500">
        <Heart className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p className="text-lg mb-2">No lists created yet</p>
        <p className="text-sm">Create lists to organize props for different productions</p>
        </div>
        )}
      </ViewLayout>

      {/* Create List Modal */}
      {showCreateModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
      <h3 className="font-semibold text-lg mb-4">Create New List</h3>
      <div className="space-y-4">
      <div>
      <label className="block text-sm font-medium mb-1">List Name *</label>
      <input
      type="text"
      value={newListName}
      onChange={(e) => setNewListName(e.target.value)}
        placeholder="e.g., Hamlet Production"
          className="w-full border border-gray-300 rounded px-3 py-2"
        />
      </div>
      <div>
      <label className="block text-sm font-medium mb-1">Description</label>
      <textarea
      value={newListDescription}
      onChange={(e) => setNewListDescription(e.target.value)}
        placeholder="Brief description of this list..."
          className="w-full border border-gray-300 rounded px-3 py-2 h-20"
        />
      </div>
      <div className="flex gap-2">
      <Button
        onClick={handleCreateList}
        variant="primary"
        size="compact"
        className="flex-1"
      >
        Create List
      </Button>
      <Button
        onClick={() => setShowCreateModal(false)}
        variant="secondary"
        size="compact"
        className="flex-1"
      >
          Cancel
          </Button>
          </div>
          </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListsView;